import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Check, Download, Loader2, Share2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { appFetch } from '@/shared/api/app-client'
import type { ApiResponse, ClippingJobResult, Template, Variant } from '@/shared/api/app-types'
import { ClipRangePlayer } from '@/features/app-shell/canvases/clip-results-canvas'
import { renderClipToMp4, type ClipRenderConfig } from '@/features/app-shell/utils/clip-render'
import { triggerDownload } from '@/features/assistant/tools/export-tools'
import { PublishClipDialog } from '@/features/app-shell/components/publish-clip-dialog'

function formatDuration(durationMs: number) {
  const seconds = Math.max(0, Math.round(durationMs / 1000))
  return seconds >= 60 ? `${Math.floor(seconds / 60)}分${seconds % 60}秒` : `${seconds}秒`
}

const captionSteps = ['准备视频素材', '提取音频', '识别语音内容', '生成时间轴字幕', '应用字幕样式与品牌', '渲染完整成片']

function CaptionProgress({ activeStep, completed }: { activeStep: number; completed?: boolean }) {
  return <div className="space-y-3 rounded-2xl border border-border bg-card p-5 sm:p-6">
    {captionSteps.map((label, index) => {
      const done = completed || index < activeStep
      const active = !completed && index === activeStep
      return <div key={label} className="flex items-center gap-3">
        {done ? <Check className="size-5 text-primary" /> : active ? <Loader2 className="size-5 animate-spin text-primary" /> : <span className="size-5 rounded-full border-2 border-muted" />}
        <span className={done || active ? 'text-sm font-medium text-foreground' : 'text-sm text-muted-foreground'}>{label}</span>
      </div>
    })}
  </div>
}

export function CaptionResultsCanvas({ taskId }: { taskId: string }) {
  const [job, setJob] = useState<ClippingJobResult | null>(null)
  const [template, setTemplate] = useState<Template | null>(null)
  const [variant, setVariant] = useState<Variant | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [rendering, setRendering] = useState(false)
  const [renderProgress, setRenderProgress] = useState(0)
  const [renderedVideo, setRenderedVideo] = useState<{ blob: Blob; url: string } | null>(null)
  const [publishOpen, setPublishOpen] = useState(false)
  const [templateLoading, setTemplateLoading] = useState(false)
  const renderStartedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    let timer: number | null = null
    let terminal = false
    const load = async () => {
      try {
        const response = await appFetch<ApiResponse<ClippingJobResult>>(`/api/clipping/jobs/${taskId}`)
        if (!cancelled) {
          setJob(response.data)
          if (response.data.status === 'failed') setError(response.data.error ?? '字幕处理失败')
          if (['completed', 'failed'].includes(response.data.status)) {
            terminal = true
            if (timer !== null) {
              window.clearInterval(timer)
              timer = null
            }
          }
        }
      } catch (reason) {
        if (!cancelled) setError(reason instanceof Error ? reason.message : '读取字幕任务失败')
      }
    }
    void load().finally(() => {
      // 已完成的历史任务不再多轮拉取，避免更新相同 job 对象时意外重启本地渲染。
      if (!cancelled && !terminal) timer = window.setInterval(() => void load(), 3_000)
    })
    return () => { cancelled = true; if (timer !== null) window.clearInterval(timer) }
  }, [taskId])

  useEffect(() => {
    if (!job?.brandTemplateId) { setTemplate(null); return }
    let cancelled = false
    setTemplateLoading(true)
    void appFetch<ApiResponse<Template>>(`/api/templates/${job.brandTemplateId}`)
      .then((response) => { if (!cancelled) setTemplate(response.data) })
      .catch(() => { if (!cancelled) setTemplate(null) })
      .finally(() => { if (!cancelled) setTemplateLoading(false) })
    return () => { cancelled = true }
  }, [job?.brandTemplateId])

  const durationMs = job?.transcript?.durationMs ?? 0
  const fullVideo = useMemo(() => ({
    title: '完整字幕成片',
    startMs: 0,
    endMs: durationMs,
    score: 10,
    reason: '已根据语音识别结果生成字幕。',
  }), [durationMs])
  const config: ClipRenderConfig = useMemo(() => ({
    src: job?.videoUrl ?? '',
    style: (template?.config?.captionStyleKey as string | undefined) ?? job?.captionStyle ?? undefined,
    resolution: job?.resolution ?? '720',
    brandTemplateId: template?.id ?? job?.brandTemplateId ?? null,
    brandTemplateName: template?.templateName ?? job?.brandTemplateName ?? null,
    captionConfig: (template?.config?.captions as Record<string, unknown> | undefined) ?? null,
    templateConfig: template?.config ?? null,
  }), [job, template])

  useEffect(() => {
    if (!job?.projectId || job.status !== 'completed') return
    let cancelled = false
    void appFetch<ApiResponse<{ items: Variant[] }>>(`/api/projects/${job.projectId}/variants?variantType=caption`)
      .then(async (response) => {
        if (cancelled) return
        const existing = response.data.items[0]
        if (existing) { setVariant(existing); return }
        const created = await appFetch<ApiResponse<Variant>>(`/api/projects/${job.projectId}/variants`, {
          method: 'POST',
          body: JSON.stringify({ variantType: 'caption', title: '完整字幕成片', status: 'review', settings: { taskId, captionStyle: config.style } }),
        })
        if (!cancelled) setVariant(created.data)
      })
      .catch(() => {
        // 分享操作会再次提示具体原因；不能因版本记录异常阻止下载成片。
      })
    return () => { cancelled = true }
  }, [config.style, job?.projectId, job?.status, taskId])

  useEffect(() => {
    if (job?.status !== 'completed' || !job.transcript || !config.src || durationMs <= 0 || templateLoading || renderStartedRef.current) return
    const controller = new AbortController()
    renderStartedRef.current = true
    setRendering(true)
    setRenderProgress(0)
    void renderClipToMp4(fullVideo, job.transcript.sentences, config, {
      signal: controller.signal,
      words: job.transcript.words,
      onProgress: (progress) => { if (!controller.signal.aborted) setRenderProgress(Math.round(progress.progress)) },
    })
      .then((result) => {
        if (!controller.signal.aborted) setRenderedVideo({ blob: result.blob, url: URL.createObjectURL(result.blob) })
      })
      .catch((reason) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : '字幕成片渲染失败')
      })
      .finally(() => { if (!controller.signal.aborted) setRendering(false) })
    return () => {
      // 此 effect 不依赖 rendering / renderedVideo，避免 setState 本身把正在进行的
      // WebCodecs 任务取消并从 0% 重启，造成页面闪烁。
      controller.abort()
    }
  }, [config, durationMs, fullVideo, job?.status, job?.transcript, templateLoading])

  useEffect(() => () => { if (renderedVideo) URL.revokeObjectURL(renderedVideo.url) }, [renderedVideo])

  const processing = !job || ['transcribing', 'analyzing'].includes(job.status)
  const captionEnabled = Boolean(config.style)
  const activeStep = job?.status === 'analyzing' ? 3 : job?.status === 'completed' ? (renderedVideo ? 6 : 5) : 2

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:px-7 sm:py-10">
      <div className="mb-7 flex items-center gap-4"><Link to="/app" className="inline-flex size-9 items-center justify-center rounded-lg border border-border bg-card text-foreground hover:bg-accent"><ArrowLeft className="size-4" /></Link><div><h1 className="text-2xl font-semibold tracking-tight text-foreground">{processing ? '正在制作字幕成片' : '字幕成片'}</h1><p className="mt-1 text-sm text-muted-foreground">{processing ? '转录完成后会自动应用所选字幕样式与品牌模板，生成完整成片。' : '已按所选字幕样式与品牌模板渲染完整视频。'}</p></div></div>

      {error ? <div className="mb-5 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</div> : null}
      {processing ? <div className="mx-auto max-w-2xl"><div className="mb-5 flex justify-end"><span className="text-sm font-semibold text-primary">约 2–5 分钟</span></div><CaptionProgress activeStep={activeStep} /></div> : null}
      {job?.status === 'completed' && job.transcript ? <div className="space-y-5">
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><div><h2 className="font-semibold text-foreground">完整视频</h2><p className="mt-1 text-xs text-muted-foreground">{formatDuration(durationMs)} · {captionEnabled ? `字幕样式：${config.style}` : '未选择字幕样式，本次不烧录字幕'}</p></div><span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"><Sparkles className="size-3.5" />字幕与品牌已应用</span></div>
          {renderedVideo ? <video src={renderedVideo.url} controls className="mx-auto max-h-[620px] w-full rounded-xl bg-black" /> : <ClipRangePlayer src={config.src} startMs={0} endMs={durationMs} sentences={job.transcript.sentences} config={config} />}
          {rendering ? <div className="mt-5"><CaptionProgress activeStep={5} /><div className="mt-4"><div className="flex justify-between text-xs text-muted-foreground"><span>正在渲染字幕成片，请保持本页面打开</span><span>{renderProgress}%</span></div><div className="mt-2 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${renderProgress}%` }} /></div></div></div> : null}
          {renderedVideo ? <div className="mt-5"><CaptionProgress activeStep={6} completed /></div> : null}
        </div>
        <div className="flex flex-wrap justify-end gap-3"><Button variant="outline" disabled={!renderedVideo} onClick={() => renderedVideo && triggerDownload(renderedVideo.blob, 'jian-caption-video.mp4')} className="gap-2"><Download className="size-4" />下载成片</Button><Button disabled={!renderedVideo || !variant} onClick={() => setPublishOpen(true)} className="gap-2"><Share2 className="size-4" />发布与分享</Button></div>
      </div> : null}
      <PublishClipDialog open={publishOpen} onOpenChange={setPublishOpen} title="字幕成片" projectId={job?.projectId ?? null} variantId={variant?.id ?? null} generateShareVideo={async () => { if (!renderedVideo) throw new Error('字幕成片尚未渲染完成'); return renderedVideo.blob }} />
    </div>
  )
}
